import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Gauge, Fuel, Settings2, ChevronRight } from 'lucide-react';
import { resolveImageUrl } from '../utils/imageUrl';

const CarCard = ({ car, delay = 0 }) => {
  const getStatusBadge = (status) => {
    const styles = {
      'Disponível': 'badge-available',
      'Reservado': 'badge-reserved',
      'Vendido': 'badge-sold'
    };
    return styles[status] || styles['Disponível'];
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('pt-PT').format(mileage);
  };

  const defaultImage = 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800';

  return (
    <Link 
      to={`/viatura/${car.id}`}
      className={`block bg-[#12141D] border border-zinc-800 hover:border-zinc-600 transition-all duration-300 group animate-fade-in-delay-${Math.min(delay, 3)}`}
      data-testid={`car-card-${car.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <img 
          src={car.images && car.images.length > 0 ? resolveImageUrl(car.images[0]) : defaultImage}
          alt={`${car.brand} ${car.model}`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        
        {/* Status Badge */}
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${getStatusBadge(car.status)}`}>
            {car.status}
          </span>
        </div>

        {/* Promotion Triangle Badge */}
        {car.promotion && (
          <div className="absolute top-0 right-0 w-0 h-0 border-t-[80px] border-t-red-600 border-l-[80px] border-l-transparent">
            <span className="absolute -top-[70px] right-[5px] text-white text-xs font-bold transform rotate-45 whitespace-nowrap">
              {car.promotion}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-medium text-white mb-4 group-hover:text-red-500 transition-colors">
          {car.brand} {car.model}
        </h3>
        
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <Calendar size={16} />
            <span>{car.year}</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <Gauge size={16} />
            <span>{formatMileage(car.mileage)} km</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <Fuel size={16} />
            <span>{car.fuel}</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <Settings2 size={16} />
            <span>{car.transmission}</span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
          <span className="text-2xl font-semibold text-white">
            {formatPrice(car.price)}
          </span>
          <span className="text-red-500 flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all">
            Ver detalhes
            <ChevronRight size={16} />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default CarCard;
